import React, { useEffect } from "react";

import BleedWrapper from "theme/components/wrappers/BleedWrapper";
import Breadcrumbs from "theme/components/Breadcrumbs";
import CategorySlider from "theme/components/contentBlocks/CategorySlider";
import CmsModules from "theme/libs/wordpress/content/CmsModules";
import LongerDescription from "theme/components/LongerDescription";
import PageWrapper from "theme/components/wrappers/PageWrapper";
import PropTypes from "prop-types";
import activeSetting from "theme/libs/wordpress/utils/activeSetting";
import getQueryParams from "core-web/utils/getQueryParams";
import { inBrowser } from "core-web/constants";
import setCategoryTree from "core-web/state/models/Products/dispatches/setCategoryTree";
import { sortCategoriesByNavigation } from "core-web/utils/sortCategoriesByNavigation";
import transformHeadingGroup from "theme/libs/wordpress/utils/transformHeadingGroup";
import useBelowBreakpoint from "core-web/hooks/useBelowBreakpoint";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

const Category = ({ data }) => {
    const navigation = useSelector(({ header }) => header.data.navigation?.main_navigation);
    const {
        id: currentId,
        parent_id: parentId,
        title,
        hero_content: heroContent,
        page_content: pageContent,
        settings,
        children: categoryChildren,
        siblings: categorySiblings,
        permalink,
    } = data;

    const { t } = useTranslation();
    const location = useLocation();
    const isMobile = useBelowBreakpoint("tabletLg");
    const device = isMobile ? "mobile" : "desktop";

    const {
        heading: textHeroHeading,
        paragraph: textHeroParagraph,
        settings: activeTextHeroSettings,
    } = heroContent?.[0]?.data?.data || {};
    const description = (activeSetting(activeTextHeroSettings, "paragraph") && textHeroParagraph) || [];
    const { text: textHeroHeadingText } = transformHeadingGroup(textHeroHeading, device);

    //if length check is changed, it should be updated in TextHero as well
    const isDescriptionTooLong = description.length > 1 || description[0]?.length > 300;

    const productGrid = pageContent?.find((item) => item.layout === "product_grid");
    const productFilters = productGrid?.data?.data?.product_filters;
    const categoryFilter = productFilters && productFilters.find((filter) => filter.layout === "category_filter");

    const categoryTreeIsFetched = useSelector(({ products }) => products.categoryTree.length > 0);

    useEffect(() => {
        if (inBrowser && !categoryTreeIsFetched && !!categoryFilter) {
            setCategoryTree();
        }
    }, [categoryTreeIsFetched]);

    const breadcrumbs = [{ permalink: "/", title: t("breadcrumbs.home") }];

    if (data.breadcrumbs?.length) {
        breadcrumbs.push(...data.breadcrumbs);
    }

    breadcrumbs.push({ permalink: null, title });

    const sortedCategories = sortCategoriesByNavigation(
        categoryChildren.length ? categoryChildren : categorySiblings,
        navigation,
        categoryChildren.length || !parentId ? currentId : parentId,
    );

    const query = getQueryParams(location);
    const isPaginatedPage = query.page && query.page > 1;

    return (
        <PageWrapper>
            {breadcrumbs && <Breadcrumbs breadcrumbs={breadcrumbs} />}
            <CmsModules
                isHero
                data={
                    heroContent && Array.isArray(heroContent) && heroContent.length > 0
                        ? heroContent[0]
                        : { layout: "hero_placeholder" }
                }
            />

            {!!sortedCategories?.length && (
                <BleedWrapper as="section">
                    <CategorySlider categories={sortedCategories} parentTitle={title} withImages />
                </BleedWrapper>
            )}

            <CmsModules data={pageContent} />

            {isDescriptionTooLong && !isPaginatedPage && (
                <div id="longer-description">
                    <LongerDescription heading={textHeroHeadingText} description={description} />
                </div>
            )}
        </PageWrapper>
    );
};

Category.propTypes = {
    data: PropTypes.object.isRequired,
};

export default Category;
